import { extractCurrencyInfo } from "../../../../../common/helpers/currency";
import type { RawCart } from "../../../../../types";
import type { TransformedCart } from "../../types/cartApiTypes";
import {
  transformAddress,
  transformCartItem,
  transformCartTotals,
  transformShippingRates,
  transformCoupons,
  transformFreeShippingData,
} from "./cartDataTransformationHelpers";

// Define clear types for the dependencies we will pass in
interface LanguageInfo {
  currentLanguage: string;
  shouldTranslate: boolean;
}

/**
 * Transforms the entire raw cart object from the API by orchestrating smaller helper functions.
 * @param cart - The raw cart object from the WC Store API.
 * @param languageInfo - An object containing language context for translations.
 * @returns A transformed cart object ready for the UI.
 */
export const transformCart = (
  cart: RawCart,
  languageInfo: LanguageInfo
): TransformedCart => {
  // Extract currency info once from the main totals object.
  const currencyInfo = extractCurrencyInfo(cart.totals);

  // --- Call the helper to handle free shipping data ---
  const freeShippingData = transformFreeShippingData(cart);

  // --- Extract Additional Billing Info Extension ---
  const additionalBillingInfo = cart.extensions?.additional_billing_info;
  const billingExtras = additionalBillingInfo
    ? {
        customerType: additionalBillingInfo.customer_type,
        tckn: additionalBillingInfo.tckn,
        vkn: additionalBillingInfo.vkn,
      }
    : undefined;

  // --- DELIVERY ESTIMATION MAPPING ---
  const deliveryRaw = cart.extensions?.delivery_estimation;
  const deliveryEstimation = deliveryRaw
    ? {
        minDate: deliveryRaw.min_date,
        maxDate: deliveryRaw.max_date,
        minDays: deliveryRaw.min_days,
        maxDays: deliveryRaw.max_days,
        displayString: deliveryRaw.display_string,
      }
    : undefined;

  // --- Assemble the final transformed cart object ---
  return {
    // --- 0. Core Meta ---
    currencyCode: cart.totals.currency_code,

    // --- 1. Cart Contents ---
    items: cart.items.map((item) => transformCartItem(item, languageInfo)),
    itemsCount: cart.items_count,
    itemsWeight: cart.items_weight,

    // --- 2. Cart Totals & Pricing ---
    totals: transformCartTotals(cart.totals, cart.shipping_rates),

    // --- 3. Applied Discounts & Fees ---
    coupons: transformCoupons(cart.coupons),
    fees: cart.fees,

    // --- 4. Shipping & Address Information ---
    shippingAddress: transformAddress(cart.shipping_address),
    // Pass the extras only to billing address
    billingAddress: transformAddress(cart.billing_address, billingExtras),
    needsShipping: cart.needs_shipping,
    hasCalculatedShipping: cart.has_calculated_shipping,
    shippingRates: transformShippingRates(cart.shipping_rates, currencyInfo),
    ...freeShippingData, // Spread all properties from the free shipping helper

    // --- 5. Payment & Status ---
    needsPayment: cart.needs_payment,
    errors: cart.errors,

    // --- 6. Related Data ---
    // Direct assignment as IDs
    crossSellIds: cart.cross_sells,

    // --- 7. Custom Extensions ---
    deliveryEstimation: deliveryEstimation, // Map the extracted data here
  };
};
